import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { AuthMenu } from "@/components/AuthMenu";
import { useCurrentUser } from "@/hooks/useAuth";
import { useRemoveSavedSearch, useSavedSearches } from "@/hooks/useUserData";

export function SavedSearchesPage() {
  const { data: user, isLoading: authLoading } = useCurrentUser();
  const { data: searches = [], isLoading } = useSavedSearches();
  const { mutate: removeSaved } = useRemoveSavedSearch();

  if (authLoading) return null;

  if (!user) {
    return (
      <CenterLayout>
        <p className="text-zinc-400 text-sm">
          Saved searches are tied to an account. Sign in to access yours.
        </p>
        <div className="flex gap-2 justify-center pt-2">
          <Link
            to="/login"
            className="bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded text-sm"
          >
            Log in
          </Link>
          <Link
            to="/register"
            className="bg-surface-2 hover:bg-surface-3 text-zinc-300 px-4 py-2 rounded text-sm"
          >
            Register
          </Link>
        </div>
      </CenterLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>Saved searches — e621 Animation Finder</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-surface-0 flex flex-col">
        <div className="sticky top-0 z-30 bg-surface-0/95 backdrop-blur border-b border-surface-2 px-4 py-3">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <Link
              to="/"
              className="text-sm text-zinc-400 hover:text-accent-light min-h-[40px] inline-flex items-center"
            >
              ← Search
            </Link>
            <h1 className="text-sm font-semibold">Saved searches</h1>
            <span className="text-xs text-zinc-600">
              {searches.length} bookmark{searches.length === 1 ? "" : "s"}
            </span>
            <div className="ml-auto">
              <AuthMenu />
            </div>
          </div>
        </div>

        <div className="flex-1 px-4 py-6 max-w-2xl mx-auto w-full">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-surface-2 h-12 rounded animate-pulse" />
              ))}
            </div>
          ) : searches.length === 0 ? (
            <div className="text-center py-20 text-zinc-500">
              <p className="text-lg">No saved searches yet</p>
              <p className="text-sm mt-1">
                Click the <code className="text-zinc-400">☆ save</code> button on the
                search page to bookmark a query.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {searches.map((s) => (
                <li
                  key={s.query}
                  className="flex items-center gap-3 bg-surface-1 border border-surface-3 rounded-lg px-3 py-2.5"
                >
                  <Link
                    to={`/search?q=${encodeURIComponent(s.query)}`}
                    className="flex-1 min-w-0 group"
                  >
                    <code className="text-sm font-mono text-accent-light group-hover:underline truncate block">
                      {s.query}
                    </code>
                    {s.label && (
                      <span className="text-[10px] text-zinc-500">{s.label}</span>
                    )}
                  </Link>
                  <span className="text-[10px] text-zinc-600 shrink-0">
                    {new Date(s.saved_at).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => removeSaved(s.query)}
                    className="text-zinc-500 hover:text-red-400 text-lg shrink-0 w-10 h-10 flex items-center justify-center rounded hover:bg-surface-2"
                    aria-label={`Remove saved search "${s.query}"`}
                    title="Remove"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

function CenterLayout({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Helmet>
        <title>Saved searches — e621 Animation Finder</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="min-h-screen bg-surface-0 flex items-center justify-center px-6">
        <div className="max-w-sm w-full bg-surface-1 border border-surface-3 rounded-lg p-6 text-center space-y-3">
          <h1 className="text-lg font-semibold">Saved searches</h1>
          {children}
        </div>
      </div>
    </>
  );
}
